import React from 'react'
import styled from '~/app/components/styled'
import {
  zIndexExtra,
  zIndexExtraHigh,
  zIndexHigh,
  zIndexLow,
  zIndexMax,
  zIndexMiddle,
  zIndexVeryHigh,
} from '~/app/components/styles/units'
import { Icon_arrowless_fill } from '@yanolja-rnd/ya-com-system-icon'

/**
 * FloatingActionButton component
 * author: soojin.oh
 */

// type definitions
type TZIndex = 'low' | 'middle' | 'high' | 'veryHigh' | 'extra' | 'extraHigh' | 'max' | 'unset'

const zIndexMap: { [key in TZIndex]: number | string } = {
  low: zIndexLow,
  middle: zIndexMiddle,
  high: zIndexHigh,
  veryHigh: zIndexVeryHigh,
  extra: zIndexExtra,
  extraHigh: zIndexExtraHigh,
  max: zIndexMax,
  unset: 'unset',
}

type GoToTopProps = {
  bottom?: number
  onClick: () => void
  isOpened: boolean
  zIndex?: TZIndex
}

// styled components
const Container = styled.div<{ bottom?: number; zIndex: TZIndex }>`
  position: fixed;
  right: 2rem;
  width: 4.8rem;
  height: 4.8rem;
  border-radius: 50%;
  cursor: pointer;
  bottom: 0;
  z-index: ${({ zIndex }) => zIndexMap[zIndex]};
  padding: 0 0.2rem 0.4rem;

  &.open {
    opacity: 1;
    bottom: calc(${({ bottom }) => bottom || 2}rem + var(--safe-area-bottom));
    transition: all 0.2s cubic-bezier(0, 1, 0.7, 1);
  }

  &.close {
    opacity: 0;
    bottom: 0;
    transition: all 0.2s cubic-bezier(1, 0, 1, 0.7);
  }

  @media (min-width: 768px) {
    right: calc((100vw - 768px) / 2 + 20px);
  }

  .wrapper {
    height: 100%;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.color.white};
  }
`

const Button = styled.button`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  box-shadow: 0 0.2rem 0.3rem 0 ${({ theme }) => theme.color.black_op16};
  border: solid 0.05rem ${({ theme }) => theme.color.black_op16};
  display: flex;
  align-items: center;

  &:hover {
    @media (hover: hover) and (pointer: fine) {
      background-color: ${({ theme }) => theme.color.black_op5};
    }
  }

  &:active {
    background-color: ${({ theme }) => theme.color.black_op10};
  }
`

const Icon = styled(Icon_arrowless_fill)`
  flex: 1;
`

/**
 * GoToTop component
 * @link https://yanolja.atlassian.net/wiki/spaces/UXD/pages/41034774/go+to+top+UI+Animation
 */
const GoToTop: React.FunctionComponent<GoToTopProps> = function ({
  bottom,
  onClick,
  isOpened,
  zIndex = 'high',
}) {
  return (
    <Container
      bottom={bottom}
      zIndex={zIndex}
      onClick={onClick}
      className={isOpened ? 'open' : 'close'}
    >
      <div className={'wrapper'}>
        <Button>
          <Icon size={2} unit={'rem'} />
        </Button>
      </div>
    </Container>
  )
}

export default GoToTop
